import {
  Attachment,
  AttachmentModel,
} from '@digital-platform/attachments/domain';
import { DocusignData } from '@digital-platform/docusign/domain';
import { DomainModelHelper } from '@digital-platform/shared/domain';
import {
  SignNowDocument,
  SignNowDocumentModel,
} from '@digital-platform/sign-now/domain';
import {
  AdditionalSupplement,
  LabAnalyticalAttachment,
  RecertificationDetails,
} from '../interfaces';
import { SafetyDataSheet } from '../interfaces/safety-data-sheet.interface';
import { AdditionalSupplementModel } from './additional-supplement.model';
import { LabAnalyticalAttachmentModel } from './lab-analytical-attachment.model';
import { SafetyDataSheetModel } from './safety-data-sheet.model';

export class RecertificationDetailsModel implements RecertificationDetails {
  public static DEFAULT_CONFIG: RecertificationDetails = {
    wasteProfileId: null,
    hasBreakingChanges: null,
    hasUpdatedAnalyticalAnalysis: null,
    hasUpdatedMsds: null,
    hasAdditionalSupplements: null,
    hasStateAttachments: null,
    hasInternalOnlyAttachment: null,
    hasChangeRecertification: null,
    labAnalyticalAttachments: [],
    safetyDataSheets: [],
    additionalSupplements: [],
    stateAttachments: [],
    isSigned: false,
    signNowDocumentId: '',
    signedName: '',
    signedTitle: '',
    signedEmail: '',
    signedCompany: '',
    signedDate: null,
    signedDocument: null,
    signNowDocument: SignNowDocumentModel.DEFAULT_CONFIG,
    docusignData: null,
    internalOnlyAttachments: [],
    isChosenSigner: false,
    archiveDate: null,
  };

  public readonly wasteProfileId: string;
  public readonly hasBreakingChanges: boolean;
  public readonly hasUpdatedAnalyticalAnalysis: boolean;
  public readonly hasAdditionalSupplements: boolean;
  public readonly hasStateAttachments: boolean;
  public readonly hasInternalOnlyAttachments: boolean;
  public readonly hasUpdatedMsds: boolean;
  public readonly hasChangeRecertification: boolean;
  public readonly labAnalyticalAttachments: LabAnalyticalAttachment[];
  public readonly safetyDataSheets: SafetyDataSheet[];
  public readonly additionalSupplements: AdditionalSupplement[];
  public readonly stateAttachments: Attachment[];
  public readonly internalOnlyAttachments: Attachment[];
  public readonly isSigned: boolean;
  public readonly signNowDocumentId: string;
  public readonly signedName: string;
  public readonly signedTitle: string;
  public readonly signedEmail: string;
  public readonly signedCompany: string;
  public readonly signedDate: Date;
  public readonly signedDocument: Attachment;
  public readonly signNowDocument: SignNowDocument;
  public readonly docusignData: DocusignData;
  public readonly isChosenSigner: boolean;
  public readonly archiveDate?: Date;

  constructor(config?: RecertificationDetails) {
    this.wasteProfileId = config
      ? DomainModelHelper.ensureDataIsString(config.wasteProfileId)
      : RecertificationDetailsModel.DEFAULT_CONFIG.wasteProfileId;
    this.hasBreakingChanges = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasBreakingChanges)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasBreakingChanges;
    this.hasUpdatedAnalyticalAnalysis = config
      ? DomainModelHelper.ensureDataIsBoolean(
          config.hasUpdatedAnalyticalAnalysis
        )
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasUpdatedAnalyticalAnalysis;
    this.hasUpdatedMsds = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasUpdatedMsds)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasUpdatedMsds;
    this.hasChangeRecertification = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasChangeRecertification)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasChangeRecertification;
    this.hasAdditionalSupplements = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasAdditionalSupplements)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasAdditionalSupplements;
    this.hasStateAttachments = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasStateAttachments)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasStateAttachments;
    this.hasInternalOnlyAttachments = config
      ? DomainModelHelper.ensureDataIsBoolean(config.hasInternalOnlyAttachment)
      : RecertificationDetailsModel.DEFAULT_CONFIG.hasInternalOnlyAttachment;
    this.labAnalyticalAttachments = config
      ? this.createLabAnalyticalAttachments(config.labAnalyticalAttachments)
      : RecertificationDetailsModel.DEFAULT_CONFIG.labAnalyticalAttachments;
    this.safetyDataSheets = config
      ? this.createSafetyDataSheets(config.safetyDataSheets)
      : RecertificationDetailsModel.DEFAULT_CONFIG.safetyDataSheets;
    this.additionalSupplements = config
      ? this.createAdditionalSupplements(config.additionalSupplements)
      : RecertificationDetailsModel.DEFAULT_CONFIG.additionalSupplements;
    this.stateAttachments = config ? config.stateAttachments : null;
    this.isSigned = config
      ? DomainModelHelper.ensureDataIsBoolean(config.isSigned)
      : RecertificationDetailsModel.DEFAULT_CONFIG.isSigned;
    this.internalOnlyAttachments = config
      ? config.internalOnlyAttachments
      : null;
    this.isSigned = config
      ? DomainModelHelper.ensureDataIsBoolean(config.isSigned)
      : RecertificationDetailsModel.DEFAULT_CONFIG.isSigned;
    this.signNowDocumentId = config
      ? DomainModelHelper.ensureDataIsString(config.signNowDocumentId)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signNowDocumentId;
    this.signedName = config
      ? DomainModelHelper.ensureDataIsString(config.signedName)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedName;
    this.signedTitle = config
      ? DomainModelHelper.ensureDataIsString(config.signedTitle)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedTitle;
    this.signedEmail = config
      ? DomainModelHelper.ensureDataIsString(config.signedEmail)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedEmail;
    this.signedCompany = config
      ? DomainModelHelper.ensureDataIsString(config.signedCompany)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedCompany;
    this.signedDate = config
      ? DomainModelHelper.ensureDataIsDate(config.signedDate)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedDate;
    this.signedDocument = config
      ? new AttachmentModel(config.signedDocument)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signedDocument;
    this.signNowDocument = config
      ? new SignNowDocumentModel(config.signNowDocument)
      : RecertificationDetailsModel.DEFAULT_CONFIG.signNowDocument;
    this.docusignData = config ? config.docusignData : null;
    this.isChosenSigner = config
      ? DomainModelHelper.ensureDataIsBoolean(config.isChosenSigner)
      : RecertificationDetailsModel.DEFAULT_CONFIG.isChosenSigner;
    this.archiveDate = config
      ? DomainModelHelper.ensureDataIsDate(config.archiveDate)
      : RecertificationDetailsModel.DEFAULT_CONFIG.archiveDate;
  }
  hasInternalOnlyAttachment: boolean;

  protected createLabAnalyticalAttachments(
    labAnalyticalAttachments: LabAnalyticalAttachment[]
  ): LabAnalyticalAttachment[] {
    if (!Array.isArray(labAnalyticalAttachments)) {
      return [];
    }

    return labAnalyticalAttachments
      .filter((labAnalyticalAttachment) => !!labAnalyticalAttachment)
      .map(
        (labAnalyticalAttachment) =>
          new LabAnalyticalAttachmentModel(labAnalyticalAttachment)
      );
  }

  protected createSafetyDataSheets(
    safetyDataSheets: SafetyDataSheet[]
  ): SafetyDataSheet[] {
    if (!Array.isArray(safetyDataSheets)) {
      return [];
    }

    return safetyDataSheets
      .filter((safetyDataSheet) => !!safetyDataSheet)
      .map((safetyDataSheet) => new SafetyDataSheetModel(safetyDataSheet));
  }

  protected createAdditionalSupplements(
    additionalSupplements: AdditionalSupplement[]
  ): AdditionalSupplement[] {
    if (!Array.isArray(additionalSupplements)) {
      return [];
    }

    return additionalSupplements
      .filter((additionalSupplement) => !!additionalSupplement)
      .map(
        (additionalSupplement) =>
          new AdditionalSupplementModel(additionalSupplement)
      );
  }
}
